import React, { useState, useEffect, useCallback } from "react";
import Button from "components/button";
import VendorAddressAccordion from "./components/vendor-address-accordion";
import VendorBranchAccordion from "./components/vendor-branch-accordion";
import VendorInformationAccordion from "./components/vendor-information-accordion";
import useCreateCustomerForm from "hooks/customer/useCreateCustomerForm";
import ConfirmationModal from "components/shared/confirmation-modal";
import { useNavigate } from "react-router-dom";
import {
  customerPageRouteName,
} from "modules/master-data/customer/constants/routes";


const AddCustomerPage = () => {
  const navigate = useNavigate();
  const {
    t,
    openSections,
    toggleSection,
    vendorInfoForm,
    vendorBranchForm,
    vendorAddressForm,
    branchItems,
    addressItems,
    branchOptions,
    hasBillingAddress,
    editingBranchIndex,
    editingAddressIndex,
    isCreatingVendor,
    isDirty,
    handleNextFromInformation,
    handleNextFromBranch,
    handleAddBranch,
    handleEditBranch,
    handleDeleteBranch,
    handleAddAddress,
    handleEditAddress,
    handleDeleteAddress,
    handleSaveDraft,
    handleSave,
  } = useCreateCustomerForm();

  const getTranslation = (
  key: string,
  fallback: string,
  options?: Record<string, unknown>,
) => {
  // @ts-expect-error temporary i18n key typing limitation
  const val = t(key, options);

  return val === key ? fallback : val;
};

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  useEffect(() => {
    const handleGlobalBack = (e: Event) => {
      if (isDirty) {
        e.preventDefault();
        setShowUnsavedModal(true);
      }
    };
    window.addEventListener("global-back-button-click", handleGlobalBack);
    return () => window.removeEventListener("global-back-button-click", handleGlobalBack);
  }, [isDirty]);

  const handleDiscard = useCallback(() => {
    setShowUnsavedModal(false);
    navigate(customerPageRouteName);
  }, [navigate]);

  const handleConfirmSaveDraft = useCallback(() => {
    setShowUnsavedModal(false);
    handleSaveDraft();
  }, [handleSaveDraft]);

  const handleConfirmSave = useCallback(async () => {
    setShowSaveModal(false);
    await handleSave();
  }, [handleSave]);

  const handleSaveClick = useCallback(async () => {
    const isValidVendorInfo = await vendorInfoForm.trigger();
    if (!isValidVendorInfo) {
      toggleSection("vendorInformation");
      return;
    }
    setShowSaveModal(true);
  }, [vendorInfoForm, toggleSection]);

  return (
    <div className="flex flex-col gap-4 pb-20">
      <VendorInformationAccordion
        isOpen={openSections.vendorInformation}
        onToggle={() => toggleSection("vendorInformation")}
        onNext={handleNextFromInformation}
        form={vendorInfoForm}
        t={t}
      />

      <VendorBranchAccordion
        isOpen={openSections.vendorBranch}
        onToggle={() => toggleSection("vendorBranch")}
        onNext={handleNextFromBranch}
        onAddToList={handleAddBranch}
        onEdit={handleEditBranch}
        onDelete={handleDeleteBranch}
        branchItems={branchItems}
        editingBranchIndex={editingBranchIndex}
        form={vendorBranchForm}
        t={t}
      />

      <VendorAddressAccordion
        isOpen={openSections.vendorAddress}
        onToggle={() => toggleSection("vendorAddress")}
        onAddToList={handleAddAddress}
        onEdit={handleEditAddress}
        onDelete={handleDeleteAddress}
        addressItems={addressItems}
        branchOptions={branchOptions}
        hasBillingAddress={hasBillingAddress}
        editingAddressIndex={editingAddressIndex}
        form={vendorAddressForm}
        t={t}
      />

      <div className="sticky bottom-0 bg-white border-t border-border px-6 py-3 flex justify-end gap-2 z-20">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          onClick={handleSaveDraft}
          disabled={isCreatingVendor}
          data-testid="btn-cancel-vendor"
        >
          {t("btn-save-draft")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          onClick={handleSaveClick}
          disabled={isCreatingVendor}
          data-testid="btn-save-vendor"
        >
          {getTranslation("btn-save", "Save")}
        </Button>
      </div>

      <ConfirmationModal
        open={showUnsavedModal}
        title={getTranslation("modal-unsaved-title", "You have unsaved changes")}
        message={getTranslation("modal-unsaved-message", "This action cannot be undone. Save as draft or discard before leaving.")}
        cancelLabel={getTranslation("btn-discard-changes", "Discard Changes")}
        confirmLabel={getTranslation("btn-save-as-draft", "Save as Draft")}
        onCancel={handleDiscard}
        onConfirm={handleConfirmSaveDraft}
        icon="info"
      />

      <ConfirmationModal
        open={showSaveModal}
        title={getTranslation("modal-save-title", "Save this customer data?")}
        message={getTranslation("modal-save-message", "Are you sure you want to save this information?")}
        cancelLabel={getTranslation("btn-cancel", "Cancel")}
        confirmLabel={getTranslation("btn-save", "Save")}
        onCancel={() => setShowSaveModal(false)}
        onConfirm={handleConfirmSave}
        icon="info"
        loading={isCreatingVendor}
      />
    </div>
  );
};

export default AddCustomerPage;
